import { useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, Loader2, Pause, Pencil, Play } from 'lucide-react';
import { P } from '@vagusx/shared';
import type { OutreachCampaign } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { PageTabsList } from '@/components/custom/page-tabs-list';
import { useOutreachCampaign, useSetCampaignStatus } from '@/hooks/use-outreach';
import { useHasPermission } from '@/hooks/use-permissions';
import { FieldErrorList, LaunchChecks, refusalFrom, type Refusal } from './editor';
import { DEFAULT_TAB, type CampaignTab } from './campaign-tabs';
import { QueueTab } from './QueueTab';
import { ResultsTab } from './ResultsTab';
import { SetupSummary } from './SetupSummary';

const STATUS_VARIANT: Record<OutreachCampaign['status'], 'default' | 'secondary' | 'outline'> = {
  active: 'default',
  paused: 'secondary',
  draft: 'outline',
};

/**
 * `/campaigns/$id` (ADR-318, H): one campaign — who is in it and where they
 * are (Queue), how it is doing (Results), and what it does (Setup, read-only;
 * Edit opens the editor).
 */
export default function CampaignPage(): React.ReactElement {
  const { id } = useParams({ from: '/_authenticated/campaigns/$id/' });
  const search = useSearch({ from: '/_authenticated/campaigns/$id/' });
  const navigate = useNavigate();
  const { data: campaign, isLoading } = useOutreachCampaign(id);
  const setStatus = useSetCampaignStatus();
  // Resume shows a refusal's checks itself, so its errors are not toasted
  // globally; anything that is not a refusal still gets a toast below.
  const resume = useSetCampaignStatus({ quiet: true });
  const canEdit = useHasPermission(P.OUTREACH_EDIT);
  const canRun = useHasPermission(P.OUTREACH_RUN);
  /** Why the server would not resume it (422): the launch checks, shown here. */
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  if (isLoading || !campaign) {
    return (
      <div className="flex h-full items-center justify-center">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <p className="text-sm text-muted-foreground">Campaign not found.</p>
        )}
      </div>
    );
  }

  const tab = search.tab ?? DEFAULT_TAB;
  const hiddenUnless = (value: CampaignTab, classes: string): string =>
    value === tab ? classes : 'hidden';
  const setTab = (next: string): void =>
    void navigate({
      to: '/campaigns/$id',
      params: { id },
      search: { tab: next as CampaignTab },
      replace: true,
    });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => void navigate({ to: '/campaigns' })}
            aria-label="Back to campaigns"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold">{campaign.name}</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant={STATUS_VARIANT[campaign.status]} className="capitalize">
                {campaign.status}
              </Badge>
              {campaign.version != null && <span>v{campaign.version}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Starting and stopping is outreach:run; the server re-checks on activate. */}
          {canRun && campaign.status === 'active' && (
            <Button
              variant="outline"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ id, status: 'paused' })}
            >
              <Pause className="mr-2 h-4 w-4" />
              Pause
            </Button>
          )}
          {canRun && campaign.status === 'paused' && (
            <Button
              variant="outline"
              disabled={resume.isPending}
              onClick={() =>
                resume.mutate(
                  { id, status: 'active' },
                  {
                    onSuccess: () => setRefusal(null),
                    onError: (err) => {
                      const found = refusalFrom(err);
                      setRefusal(found);
                      if (!found)
                        toast.error(err instanceof Error ? err.message : 'Could not resume');
                    },
                  }
                )
              }
            >
              <Play className="mr-2 h-4 w-4" />
              Resume
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => void navigate({ to: '/campaigns/$id/edit', params: { id } })}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {refusal && <ResumeRefusal refusal={refusal} spec={campaign.spec} />}

      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <PageTabsList className="shrink-0 px-4 sm:px-6">
          <TabsTrigger value="queue">Queue</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
          <TabsTrigger value="setup">Setup</TabsTrigger>
        </PageTabsList>
        {/* The queue is a TablePage, which lays itself out to the edges. */}
        {/*
          Every tab stays mounted (forceMount), so the lane being worked and
          the results range survive a look at another tab; each one's queries
          run only while it shows.
        */}
        <TabsContent value="queue" forceMount className={hiddenUnless('queue', 'min-h-0 flex-1')}>
          <QueueTab campaign={campaign} canRun={canRun} active={tab === 'queue'} />
        </TabsContent>
        <TabsContent
          value="results"
          forceMount
          className={hiddenUnless('results', 'min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6')}
        >
          <ResultsTab campaignId={campaign.id} active={tab === 'results'} />
        </TabsContent>
        <TabsContent
          value="setup"
          forceMount
          className={hiddenUnless('setup', 'min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6')}
        >
          <SetupSummary campaign={campaign} active={tab === 'setup'} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Why the server would not resume the campaign: its launch checks and field errors. */
export function ResumeRefusal({
  refusal,
  spec,
}: {
  refusal: Refusal;
  spec: OutreachCampaign['spec'];
}): React.ReactElement {
  return (
    <div className="shrink-0 space-y-2 border-b bg-destructive/5 px-4 py-3 sm:px-6">
      <p className="flex items-start gap-1.5 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        Not resumed. Fix what is marked below in the editor, then resume.
      </p>
      {spec && refusal.readiness && <LaunchChecks readiness={refusal.readiness} spec={spec} />}
      {spec && refusal.fieldErrors.length > 0 && (
        <FieldErrorList errors={refusal.fieldErrors} spec={spec} />
      )}
    </div>
  );
}
